'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { useToast } from '@/components/ui'
import { ideasService } from '@/services/http/ideasService'
import type { QueryParams } from '@/utils/queryParams'

type UseSimilarIdeasParams = {
  ideaId: string
  query: QueryParams
}

export const useSimilarIdeas = ({ ideaId, query }: UseSimilarIdeasParams) => {
  const { limit = 10, page = 1, ...rest } = query
  const { toast } = useToast()

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch: refetchSimilarIdeas,
  } = useInfiniteQuery({
    queryKey: [
      'similarIdeas',
      ideaId,
      {
        limit,
        ...rest,
      },
    ],
    queryFn: async ({ pageParam = page }) =>
      ideasService.getSimilarIdeas({
        ideaId,
        query: {
          limit,
          page: pageParam,
          ...rest,
        },
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.totalPages) {
        return lastPage.pagination.page + 1
      }

      return undefined
    },
    initialPageParam: page,
    enabled: !!ideaId,
  })

  useEffect(() => {
    if (isError) {
      toast({
        title: 'Erro ao carregar ideias similares',
        variant: 'destructive',
      })
    }
  }, [isError, toast])

  const similarIdeas = data?.pages.flatMap((page) => page.ideas) ?? []

  return {
    similarIdeas,
    isLoading,
    isLoadingNextPage: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetchSimilarIdeas,
  }
}
